import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * LA MINIATURA DE UNA SERIE, para dentro de una tarjeta de cifra.
 *
 * Cesar, 2026-10-04: «diseño top». Una cifra sola dice cuánto; la miniatura
 * dice de dónde viene. Es la lámina 19 en pequeño y con las mismas tres reglas:
 * línea FINA, sin eje ni rejilla (a este tamaño serían ruido), y **el último
 * dato en rojo**, porque es «hasta aquí llegamos» y no un punto más.
 *
 * No tiene tooltip ni etiquetas a propósito: para leer el valor de un mes está
 * el diálogo de detalle que abre la tarjeta. Aquí se mira la forma.
 *
 * `null` es un hueco, igual que en `Chart`: la línea se parte, no baja a cero.
 */
export function Sparkline({
  valores, ancho = 88, alto = 26, className,
}: { valores: Array<number | null>; ancho?: number; alto?: number; className?: string }) {
  const presentes = valores.filter((v): v is number => v != null && Number.isFinite(v))
  if (presentes.length < 2) return null

  const min = Math.min(0, ...presentes)
  const max = Math.max(0, ...presentes)
  const rango = max - min || 1
  const pad = 2
  const x = (i: number) => pad + (i * (ancho - pad * 2)) / Math.max(1, valores.length - 1)
  const y = (v: number) => alto - pad - ((v - min) / rango) * (alto - pad * 2)

  // Tramos continuos: un `null` cierra el tramo y el siguiente abre otro.
  const tramos: string[] = []
  let actual: string[] = []
  valores.forEach((v, i) => {
    if (v == null || !Number.isFinite(v)) {
      if (actual.length) tramos.push(actual.join(" "))
      actual = []
      return
    }
    actual.push(`${actual.length ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
  })
  if (actual.length) tramos.push(actual.join(" "))

  let ultimo = valores.length - 1
  while (ultimo >= 0 && (valores[ultimo] == null || !Number.isFinite(valores[ultimo] as number))) ultimo -= 1
  const vUltimo = valores[ultimo] as number

  return (
    <svg
      width={ancho}
      height={alto}
      viewBox={`0 0 ${ancho} ${alto}`}
      className={cn("shrink-0 overflow-visible", className)}
      aria-hidden="true"
      focusable="false"
    >
      {min < 0 && max > 0 ? (
        <line x1={pad} x2={ancho - pad} y1={y(0)} y2={y(0)}
              stroke="hsl(var(--border))" strokeWidth={1} />
      ) : null}
      {tramos.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="hsl(var(--chart-1))" strokeWidth={1.25}
              strokeLinejoin="round" strokeLinecap="round" />
      ))}
      <circle cx={x(ultimo)} cy={y(vUltimo)} r={2} fill="hsl(var(--chart-acento))" />
    </svg>
  )
}
